import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useLanguage, apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import PageHeader from "../components/layout/PageHeader";
import XPBar from "../components/gamification/XPBar";
import StreakCounter from "../components/gamification/StreakCounter";
import AchievementBadge from "../components/gamification/AchievementBadge";
import ChallengeCard from "../components/gamification/ChallengeCard";
import LevelUpModal from "../components/gamification/LevelUpModal";
import { useGamification } from "../contexts/GamificationContext";
import { AVATAR_CHARACTERS } from "../config/characters";
import CharacterSelector from "../components/shared/CharacterSelector";
import { toast } from "sonner";
import {
  Trophy,
  Target,
  Flame,
  Award,
  Star,
  TrendingUp,
  Calendar,
  CheckCircle2,
  Sparkles,
  Crown,
  Medal,
  Swords,
  Lock,
  Share2,
  HandHeart,
  UserPlus,
  MessageCircle,
  RefreshCw
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// Helper function to get character avatar URL
const getCharacterAvatar = (avatarCharacterId) => {
  if (!avatarCharacterId) return null;
  const character = AVATAR_CHARACTERS.find(c => c.id === avatarCharacterId);
  return character?.image || null;
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const navigate = useNavigate();
  const {
    loading,
    level,
    totalXP,
    streak,
    longestStreak,
    achievements,
    totalChallengesCompleted,
    dailyChallenges,
    loadAchievements,
    loadLeaderboard,
    leaderboard
  } = useGamification();

  const [allAchievements, setAllAchievements] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [xpHistoryData, setXpHistoryData] = useState([]);
  const [friendSearchQuery, setFriendSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loadingPartner, setLoadingPartner] = useState(false);
  const [compareTarget, setCompareTarget] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [encouragements, setEncouragements] = useState([]);
  const [selectedFriendId, setSelectedFriendId] = useState("");
  const [encourageMessage, setEncourageMessage] = useState("");
  const [sendingEncouragement, setSendingEncouragement] = useState(false);
  const [sharingAchievement, setSharingAchievement] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    gender: user?.gender || "",
    avatar_character_id: user?.avatar_character_id || null
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);

      if (!profileData.name.trim()) {
        toast.error(isEnglish ? "Name is required" : "Nama harus diisi");
        return;
      }
      if (!profileData.email.trim()) {
        toast.error(isEnglish ? "Email is required" : "Email harus diisi");
        return;
      }

      const updateData = {};
      if (profileData.name !== user?.name) updateData.name = profileData.name.trim();
      if (profileData.email !== user?.email) updateData.email = profileData.email.trim();
      if (profileData.phone !== user?.phone) updateData.phone = profileData.phone.trim() || null;
      if (profileData.gender !== user?.gender) updateData.gender = profileData.gender || null;
      if (profileData.avatar_character_id !== user?.avatar_character_id) updateData.avatar_character_id = profileData.avatar_character_id;

      if (Object.keys(updateData).length === 0) {
        return;
      }

      await apiClient.patch("/auth/me", updateData);
      const updatedUser = { ...user, ...updateData };
      setUser(updatedUser);
      toast.success(isEnglish ? "Profile updated successfully" : "Profil berhasil diperbarui");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || (isEnglish ? "Failed to update profile" : "Gagal memperbarui profil");
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const buildWeekXpData = useCallback((dailyXp = []) => {
    const xpByDate = new Map(dailyXp.map((item) => [item.date, item.xp || 0]));
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    monday.setHours(12, 0, 0, 0);

    const weekData = [];
    for (let i = 0; i < 7; i += 1) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      const dateStr = dayDate.toISOString().split("T")[0];
      const dayLabel = dayDate.toLocaleDateString(isEnglish ? "en-US" : "id-ID", { weekday: "short" });
      weekData.push({
        day: dayLabel,
        xp: xpByDate.get(dateStr) || 0
      });
    }
    return weekData;
  }, [isEnglish]);

  useEffect(() => {
    const loadData = async () => {
      const [achievementsData, leaderboardData, xpHistoryResponse] = await Promise.all([
        loadAchievements(),
        loadLeaderboard(10),
        apiClient.get("/gamification/xp-history").catch(() => null)
      ]);
      setAllAchievements(achievementsData?.achievements || []);
      const dailyXp = xpHistoryResponse?.data?.daily_xp || [];
      setXpHistoryData(buildWeekXpData(dailyXp));
    };
    loadData();
  }, [loadAchievements, loadLeaderboard, buildWeekXpData]);

  useEffect(() => {
    if (activeTab !== "partner") return;
    const loadPartnerData = async () => {
      setLoadingPartner(true);
      try {
        const [requestsRes, friendsRes, messagesRes] = await Promise.all([
          apiClient.get("/friends/requests").catch(() => ({ data: { requests: [] } })),
          apiClient.get("/friends").catch(() => ({ data: { friends: [] } })),
          apiClient.get("/friends/encouragements", { params: { scope: "all" } }).catch(() => ({ data: { messages: [] } }))
        ]);
        setFriendRequests(requestsRes.data.requests || []);
        setFriends(friendsRes.data.friends || []);
        setEncouragements(messagesRes.data.messages || []);
      } finally {
        setLoadingPartner(false);
      }
    };
    loadPartnerData();
  }, [activeTab]);

  const createShareCanvas = async (drawFn, fileName, shareText) => {
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawFn(ctx, canvas);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) return;
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: shareText });
        return;
      } catch (error) {
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const drawAchievementCard = (ctx, canvas, unlockedCount) => {
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, "#7C3AED");
    gradient.addColorStop(1, "#0D9488");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "rgba(255,255,255,0.18)";
    ctx.fillRect(80, 140, 920, 720);

    ctx.fillStyle = "#FFFFFF";
    ctx.font = "700 56px sans-serif";
    ctx.fillText(isEnglish ? "Achievement Card" : "Kartu Pencapaian", 120, 220);

    ctx.font = "600 36px sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillText(user?.name || (isEnglish ? "User" : "User"), 120, 280);

    const stats = [
      { label: isEnglish ? "Level" : "Level", value: String(level || 1) },
      { label: isEnglish ? "Total XP" : "Total XP", value: (totalXP || 0).toLocaleString() },
      { label: isEnglish ? "Streak" : "Streak", value: `${streak || 0} ${isEnglish ? "days" : "hari"}` },
      { label: isEnglish ? "Achievements" : "Achievement", value: String(unlockedCount || 0) }
    ];

    let x = 140;
    let y = 360;
    stats.forEach((stat, index) => {
      ctx.fillStyle = "rgba(255,255,255,0.18)";
      ctx.fillRect(x, y, 360, 160);
      ctx.fillStyle = "rgba(255,255,255,0.75)";
      ctx.font = "500 26px sans-serif";
      ctx.fillText(stat.label, x + 20, y + 55);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "700 40px sans-serif";
      ctx.fillText(stat.value, x + 20, y + 115);
      if (index % 2 === 1) {
        x = 140;
        y += 200;
      } else {
        x += 400;
      }
    });

    ctx.fillStyle = "rgba(255,255,255,0.85)";
    ctx.font = "500 22px sans-serif";
    ctx.fillText(isEnglish ? "Shared from QuranNext" : "Dibagikan dari QuranNext", 120, 980);
  };

  const handleShareAchievement = async (unlockedCount) => {
    setSharingAchievement(true);
    await createShareCanvas(
      (ctx, canvas) => drawAchievementCard(ctx, canvas, unlockedCount),
      "qurannext-achievement.png",
      isEnglish ? "My Quran achievement progress" : "Pencapaian Quran saya"
    );
    setSharingAchievement(false);
  };

  const handleSearchUsers = async () => {
    const query = friendSearchQuery.trim();
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await apiClient.get("/users/search", { params: { query } });
      setSearchResults(response.data.users || []);
    } catch (error) {
      toast.error(isEnglish ? "Search failed" : "Pencarian gagal");
    }
  };

  const handleSendFriendRequest = async (userId) => {
    try {
      await apiClient.post(`/friends/request/${userId}`);
      toast.success(isEnglish ? "Friend request sent" : "Permintaan pertemanan terkirim");
      handleSearchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || (isEnglish ? "Failed to send request" : "Gagal mengirim permintaan");
      toast.error(msg);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await apiClient.post(`/friends/requests/${requestId}/accept`);
      toast.success(isEnglish ? "Request accepted" : "Permintaan diterima");
      setFriendRequests((prev) => prev.filter((req) => req.request_id !== requestId));
      const friendsRes = await apiClient.get("/friends").catch(() => ({ data: { friends: [] } }));
      setFriends(friendsRes.data.friends || []);
    } catch (error) {
      const msg = error.response?.data?.detail || (isEnglish ? "Failed to accept request" : "Gagal menerima permintaan");
      toast.error(msg);
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await apiClient.post(`/friends/requests/${requestId}/reject`);
      toast.success(isEnglish ? "Request rejected" : "Permintaan ditolak");
      setFriendRequests((prev) => prev.filter((req) => req.request_id !== requestId));
    } catch (error) {
      const msg = error.response?.data?.detail || (isEnglish ? "Failed to reject request" : "Gagal menolak permintaan");
      toast.error(msg);
    }
  };

  const handleCompare = async (friend) => {
    try {
      setCompareTarget(friend);
      const response = await apiClient.get(`/friends/compare/${friend.user_id}`);
      setCompareData(response.data);
    } catch (error) {
      const msg = error.response?.data?.detail || (isEnglish ? "Failed to compare progress" : "Gagal membandingkan progress");
      toast.error(msg);
    }
  };

  const handleSendEncouragement = async () => {
    const messageText = encourageMessage.trim();
    if (!selectedFriendId || !messageText) return;
    try {
      setSendingEncouragement(true);
      await apiClient.post(`/friends/encourage/${selectedFriendId}`, { message: messageText });
      setEncourageMessage("");
      const messagesRes = await apiClient.get("/friends/encouragements", { params: { scope: "all" } }).catch(() => ({ data: { messages: [] } }));
      setEncouragements(messagesRes.data.messages || []);
      toast.success(isEnglish ? "Encouragement sent" : "Semangat terkirim");
    } catch (error) {
      const msg = error.response?.data?.detail || (isEnglish ? "Failed to send encouragement" : "Gagal mengirim semangat");
      toast.error(msg);
    } finally {
      setSendingEncouragement(false);
    }
  };

  const formatMessageTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleString(isEnglish ? "en-US" : "id-ID", {
      dateStyle: "medium",
      timeStyle: "short"
    });
  };

  if (loading && !level) {
    return (
      <div className="page-container space-y-4 sm:space-y-5">
        <PageHeader title={isEnglish ? "Profile" : "Profil"} />
        <div className="space-y-3 sm:space-y-4">
          <Skeleton className="h-24 sm:h-32 w-full" />
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <Skeleton className="h-20 sm:h-24" />
            <Skeleton className="h-20 sm:h-24" />
          </div>
          <Skeleton className="h-36 sm:h-48 w-full" />
        </div>
      </div>
    );
  }

  const unlockedAchievements = allAchievements?.filter(a => a.unlocked) || [];
  const lockedAchievements = allAchievements?.filter(a => !a.unlocked) || [];
  const selectedFriend = friends.find((friend) => friend.user_id === selectedFriendId);

  return (
    <div className="page-container space-y-4 sm:space-y-5">
      <LevelUpModal />

      <PageHeader title={isEnglish ? "Profile" : "Profil"} />

      

      <Card className="islamic-gradient text-white">
        <CardContent className="p-4 sm:p-5 md:p-6 space-y-4">
          {getCharacterAvatar(user?.avatar_character_id) ? (
            <div className="w-full flex items-center justify-center">
              <img
                src={getCharacterAvatar(user?.avatar_character_id)}
                alt={user?.name || "Avatar"}
                className="w-full max-h-72 sm:max-h-80 object-contain rounded-xl bg-white/10"
              />
            </div>
          ) : (
            <div className="flex items-center gap-3 sm:gap-4">
              <Avatar className="w-16 h-16 sm:w-20 sm:h-20 border-2 sm:border-4 border-white/30 flex-shrink-0">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="text-xl sm:text-2xl bg-white/20 text-white">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <Button
                variant="secondary"
                size="sm"
                className="flex-shrink-0 text-xs sm:text-sm"
              onClick={() => navigate("/profile/edit")}
              >
                {isEnglish ? "Edit Profile" : "Ubah Profil"}
              </Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold truncate">{user?.name || "User"}</h2>
              <p className="text-sm sm:text-base text-white/80 truncate">{user?.email}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="flex-shrink-0 text-xs sm:text-sm"
              onClick={() => navigate("/profile/edit")}
            >
              {isEnglish ? "Edit Profile" : "Ubah Profil"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <Card>
          <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
            <Trophy className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-islamic-gold" />
            <p className="text-lg sm:text-xl md:text-2xl font-bold">{level}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{isEnglish ? "Level" : "Level"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
            <Star className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-primary" />
            <p className="text-lg sm:text-xl md:text-2xl font-bold">{totalXP.toLocaleString()}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Total XP</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
            <Flame className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-orange-500" />
            <p className="text-lg sm:text-xl md:text-2xl font-bold">{streak}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{isEnglish ? "Day Streak" : "Streak Hari"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
            <Target className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-green-500" />
            <p className="text-lg sm:text-xl md:text-2xl font-bold">{unlockedAchievements.length}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              {isEnglish ? "Achievements" : "Achievement"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* XP Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            {isEnglish ? "Level Progress" : "Progress Level"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <XPBar showLevel={true} showLabel={true} size="lg" />
        </CardContent>
      </Card>

      {/* Premium Custom Tabs */}
      <Card className="overflow-visible sm:overflow-hidden">
        {/* Premium Tab Header */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background -m-1 p-1 rounded-t-lg">
          <div className="flex gap-1">
            {[
              { value: "overview", label: isEnglish ? "Ringkasan" : "Ringkasan", icon: TrendingUp, badge: null },
              { value: "achievements", label: isEnglish ? "Achievement" : "Achievement", icon: Trophy, badge: unlockedAchievements.length },
              { value: "challenges", label: isEnglish ? "Tantangan" : "Tantangan", icon: Swords, badge: dailyChallenges?.length || 0 },
              { value: "partner", label: isEnglish ? "Partner" : "Partner", icon: HandHeart, badge: friendRequests.length || null }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.value;

              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`flex-1 relative flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium text-[10px] sm:text-xs md:text-sm transition-all duration-300 ${
                    isActive
                      ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-md sm:shadow-lg shadow-primary/25"
                      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  }`}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-lg sm:rounded-xl animate-pulse" />
                  )}
                  <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isActive ? "text-white" : ""} flex-shrink-0`} />
                  <span className="relative truncate">{tab.label}</span>
                  {tab.badge !== null && (
                    <Badge
                      variant={isActive ? "secondary" : "outline"}
                      className={`ml-0.5 sm:ml-1 px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] md:text-xs flex-shrink-0 ${
                        isActive
                          ? "bg-white/20 text-white border-white/30"
                          : "bg-primary/10 text-primary border-primary/20"
                      }`}
                    >
                      {tab.badge}
                    </Badge>
                  )}
                  {isActive && (
                    <Sparkles className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-400 animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <CardContent className="p-3 sm:p-4 md:p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* Streak Card */}
          <StreakCounter size="md" />

          {/* XP History Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">{isEnglish ? "XP This Week" : "XP Minggu Ini"}</CardTitle>
            </CardHeader>
            <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={xpHistoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="xp" stroke="#0D9488" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Leaderboard Preview */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-4 md:p-6">
              <CardTitle className="text-base sm:text-lg">{isEnglish ? "Leaderboard" : "Papan Peringkat"}</CardTitle>
              <Button variant="outline" size="sm" className="text-xs sm:text-sm">
                {isEnglish ? "View All" : "Lihat Semua"}
              </Button>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0">
              <div className="space-y-1.5 sm:space-y-2">
                {leaderboard.slice(0, 5).map((entry, index) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg ${
                      entry.is_current_user ? "bg-primary/10" : ""
                    }`}
                  >
                    <div
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 shadow-sm ${
                        index === 0
                          ? "bg-gradient-to-br from-yellow-400 to-yellow-600 text-white"
                          : index === 1
                          ? "bg-gradient-to-br from-gray-300 to-gray-500 text-white"
                          : index === 2
                          ? "bg-gradient-to-br from-orange-400 to-orange-600 text-white"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {entry.rank}
                    </div>
                    <Avatar className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0">
                      <AvatarImage
                        src={getCharacterAvatar(entry.avatar_character_id) || entry.picture}
                      />
                      <AvatarFallback>{entry.name?.charAt(0) || "?"}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-xs sm:text-sm truncate">{entry.name || "User"}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-sm sm:text-base text-primary">{entry.total_xp}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">XP</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                {isEnglish ? "Share Achievement" : "Bagikan Pencapaian"}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShareAchievement(unlockedAchievements.length)}
                disabled={sharingAchievement}
                className="text-xs sm:text-sm"
              >
                {sharingAchievement ? (isEnglish ? "Preparing..." : "Menyiapkan...") : (isEnglish ? "Share" : "Bagikan")}
              </Button>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <div className="rounded-xl border border-border bg-gradient-to-br from-primary/10 via-background to-background p-4 sm:p-5 space-y-2">
                <p className="text-sm sm:text-base font-semibold">{user?.name || (isEnglish ? "User" : "User")}</p>
                <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                  <div className="rounded-lg bg-background/70 p-2">
                    <p className="text-muted-foreground">{isEnglish ? "Level" : "Level"}</p>
                    <p className="font-semibold">{level || 1}</p>
                  </div>
                  <div className="rounded-lg bg-background/70 p-2">
                    <p className="text-muted-foreground">XP</p>
                    <p className="font-semibold">{totalXP.toLocaleString()}</p>
                  </div>
                  <div className="rounded-lg bg-background/70 p-2">
                    <p className="text-muted-foreground">{isEnglish ? "Streak" : "Streak"}</p>
                    <p className="font-semibold">{streak} {isEnglish ? "days" : "hari"}</p>
                  </div>
                  <div className="rounded-lg bg-background/70 p-2">
                    <p className="text-muted-foreground">{isEnglish ? "Achievements" : "Achievement"}</p>
                    <p className="font-semibold">{unlockedAchievements.length}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === "achievements" && (
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* Stats */}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20 rounded-lg sm:rounded-xl border border-yellow-200 dark:border-yellow-900 gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-md sm:shadow-lg flex-shrink-0">
                <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold truncate">
                  {isEnglish ? "Achievement Progress" : "Progress Achievement"}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  {unlockedAchievements.length} / {allAchievements?.length || 0}{" "}
                  {isEnglish ? "unlocked" : "dibuka"}
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="text-sm sm:text-base px-2 sm:px-3 md:px-4 py-1 sm:py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 shadow-md sm:shadow-lg flex-shrink-0">
              {allAchievements?.length ? Math.round((unlockedAchievements.length / allAchievements.length) * 100) : 0}%
            </Badge>
          </div>

          {/* Unlocked Achievements */}
          {unlockedAchievements.length > 0 && (
            <div>
              <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
                <Medal className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500" />
                {isEnglish ? "Unlocked" : "Dibuka"}
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
                {unlockedAchievements.map((achievement) => (
                  <AchievementBadge
                    key={achievement.id}
                    achievement={achievement}
                    size="sm"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Locked Achievements */}
          {lockedAchievements.length > 0 && (
            <div>
              <h4 className="text-xs sm:text-sm font-semibold text-muted-foreground mb-2 sm:mb-3 flex items-center gap-1.5 sm:gap-2">
                <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
                {isEnglish ? "Locked" : "Terkunci"}
              </h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
                {lockedAchievements.map((achievement) => (
                  <AchievementBadge
                    key={achievement.id}
                    achievement={achievement}
                    size="sm"
                  />
                ))}
              </div>
            </div>
          )}
            </div>
          )}

          {/* Challenges Tab */}
          {activeTab === "challenges" && (
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-lg sm:rounded-xl border border-purple-200 dark:border-purple-900 gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md sm:shadow-lg flex-shrink-0">
                <Swords className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold truncate">
                  {isEnglish ? "Today's Challenges" : "Tantangan Hari Ini"}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">
                  {dailyChallenges?.challenges?.length || 0} {isEnglish ? "challenges available" : "tantangan tersedia"}
                </p>
              </div>
            </div>
            <Badge variant="secondary" className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-md sm:shadow-lg flex-shrink-0 text-[10px] sm:text-xs px-2 sm:px-3 py-1">
              <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
              {new Date().toLocaleDateString()}
            </Badge>
          </div>

          {/* Daily Challenges List */}
          <div>
            {dailyChallenges?.challenges ? (
              <div className="space-y-2 sm:space-y-3">
                {dailyChallenges.challenges.map((challenge) => (
                  <ChallengeCard
                    key={challenge.challenge_index}
                    challenge={challenge}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-4 sm:p-6 text-center text-muted-foreground text-sm sm:text-base">
                  {isEnglish ? "No challenges available" : "Tidak ada tantangan"}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-900">
              <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
                <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-green-500" />
                <p className="text-lg sm:text-xl md:text-2xl font-bold">{totalChallengesCompleted}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {isEnglish ? "Completed" : "Selesai"}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/20 dark:to-amber-950/20 border-yellow-200 dark:border-yellow-900">
              <CardContent className="p-2.5 sm:p-3 md:p-4 text-center">
                <Award className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 mx-auto mb-1.5 sm:mb-2 text-islamic-gold" />
                <p className="text-lg sm:text-xl md:text-2xl font-bold">
                  {totalChallengesCompleted * 30}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {isEnglish ? "XP Earned" : "XP Didapat"}
                </p>
              </CardContent>
            </Card>
          </div>
            </div>
          )}

          {activeTab === "partner" && (
            <div className="space-y-4 sm:space-y-5 md:space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                    {isEnglish ? "Find Partner" : "Cari Partner"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6 space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder={isEnglish ? "Search name or email (min 3 chars)" : "Cari nama atau email (min 3 huruf)"}
                      value={friendSearchQuery}
                      onChange={(e) => setFriendSearchQuery(e.target.value)}
                    />
                    <Button variant="outline" onClick={handleSearchUsers}>
                      {isEnglish ? "Search" : "Cari"}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {searchResults.length === 0 ? (
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {isEnglish ? "No results yet" : "Belum ada hasil"}
                      </p>
                    ) : (
                      searchResults.map((result) => (
                        <div key={result.user_id} className="flex items-center gap-3 rounded-lg border border-border p-2 sm:p-3">
                          <Avatar className="w-9 h-9 sm:w-10 sm:h-10">
                            <AvatarImage src={result.picture} alt={result.name} />
                            <AvatarFallback>{result.name?.charAt(0) || "U"}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{result.name || "User"}</p>
                            <p className="text-xs text-muted-foreground">{result.is_friend ? (isEnglish ? "Already friends" : "Sudah teman") : result.has_pending_request ? (isEnglish ? "Pending request" : "Menunggu konfirmasi") : (isEnglish ? "Not connected" : "Belum terhubung")}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={result.is_friend || result.has_pending_request}
                            onClick={() => handleSendFriendRequest(result.user_id)}
                          >
                            {result.is_friend ? (isEnglish ? "Friend" : "Teman") : result.has_pending_request ? (isEnglish ? "Pending" : "Menunggu") : (isEnglish ? "Add" : "Tambah")}
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                <Card>
                  <CardHeader className="flex items-center justify-between">
                    <CardTitle className="text-base sm:text-lg">{isEnglish ? "Requests" : "Permintaan"}</CardTitle>
                    <Badge variant="secondary">{friendRequests.length}</Badge>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 md:p-6 space-y-3">
                    {loadingPartner ? (
                      <Skeleton className="h-20 w-full" />
                    ) : friendRequests.length === 0 ? (
                      <p className="text-xs sm:text-sm text-muted-foreground">{isEnglish ? "No requests" : "Tidak ada permintaan"}</p>
                    ) : (
                      friendRequests.map((req) => (
                        <div key={req.request_id} className="flex items-center gap-3 rounded-lg border border-border p-2 sm:p-3">
                          <Avatar className="w-9 h-9 sm:w-10 sm:h-10">
                            <AvatarImage src={req.from_user?.picture} alt={req.from_user?.name} />
                            <AvatarFallback>{req.from_user?.name?.charAt(0) || "U"}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{req.from_user?.name || "User"}</p>
                            <p className="text-xs text-muted-foreground">{isEnglish ? "Wants to connect" : "Ingin terhubung"}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleAcceptRequest(req.request_id)}>
                              {isEnglish ? "Accept" : "Terima"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectRequest(req.request_id)}>
                              {isEnglish ? "Reject" : "Tolak"}
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex items-center justify-between">
                    <CardTitle className="text-base sm:text-lg">{isEnglish ? "Partners" : "Partner"}</CardTitle>
                    <Badge variant="secondary">{friends.length}</Badge>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 md:p-6 space-y-3">
                    {loadingPartner ? (
                      <Skeleton className="h-20 w-full" />
                    ) : friends.length === 0 ? (
                      <p className="text-xs sm:text-sm text-muted-foreground">{isEnglish ? "No partners yet" : "Belum ada partner"}</p>
                    ) : (
                      friends.map((friend) => (
                        <div key={friend.user_id} className="rounded-lg border border-border p-3 space-y-2">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-9 h-9 sm:w-10 sm:h-10">
                              <AvatarImage src={getCharacterAvatar(friend.avatar_character_id) || friend.picture} alt={friend.name} />
                              <AvatarFallback>{friend.name?.charAt(0) || "U"}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">{friend.name || "User"}</p>
                              <p className="text-xs text-muted-foreground">{isEnglish ? `Level ${friend.level} • XP ${friend.total_xp}` : `Level ${friend.level} • XP ${friend.total_xp}`}</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handleCompare(friend)}>
                              {isEnglish ? "Compare" : "Bandingkan"}
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span>{isEnglish ? "Streak" : "Streak"}: <span className="font-semibold text-foreground">{friend.overall_streak}</span></span>
                            <span>{isEnglish ? "Achievements" : "Achievement"}: <span className="font-semibold text-foreground">{friend.achievements_count}</span></span>
                            <span>{isEnglish ? "Challenges" : "Tantangan"}: <span className="font-semibold text-foreground">{friend.total_challenges_completed}</span></span>
                          </div>
                          <Button
                            size="sm"
                            variant={selectedFriendId === friend.user_id ? "default" : "outline"}
                            onClick={() => setSelectedFriendId(friend.user_id)}
                            className="w-full"
                          >
                            {selectedFriendId === friend.user_id ? (isEnglish ? "Selected" : "Dipilih") : (isEnglish ? "Encourage" : "Kirim Semangat")}
                          </Button>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    {isEnglish ? "Mutual Encouragement" : "Saling Menyemangati"}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const messagesRes = await apiClient.get("/friends/encouragements", { params: { scope: "all" } }).catch(() => ({ data: { messages: [] } }));
                      setEncouragements(messagesRes.data.messages || []);
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {isEnglish ? "Sync" : "Sinkron"}
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6 space-y-3">
                  <div className="space-y-2">
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {selectedFriend ? (isEnglish ? `Send encouragement to ${selectedFriend.name}` : `Kirim semangat ke ${selectedFriend.name}`) : (isEnglish ? "Choose a partner first" : "Pilih partner dulu")}
                    </p>
                    <Textarea
                      placeholder={isEnglish ? "Write your message..." : "Tulis pesan semangat..."}
                      value={encourageMessage}
                      onChange={(e) => setEncourageMessage(e.target.value)}
                      disabled={!selectedFriendId}
                    />
                    <Button
                      onClick={handleSendEncouragement}
                      disabled={!selectedFriendId || sendingEncouragement || !encourageMessage.trim()}
                    >
                      {sendingEncouragement ? (isEnglish ? "Sending..." : "Mengirim...") : (isEnglish ? "Send Encouragement" : "Kirim Semangat")}
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {encouragements.length === 0 ? (
                      <p className="text-xs sm:text-sm text-muted-foreground">{isEnglish ? "No messages yet" : "Belum ada pesan"}</p>
                    ) : (
                      encouragements.slice(0, 8).map((msg) => {
                        const isMine = msg.from_user_id === user?.user_id;
                        const senderName = msg.from_user?.name || (isMine ? (isEnglish ? "You" : "Anda") : "User");
                        return (
                          <div key={msg.message_id} className={`rounded-lg border border-border p-2 sm:p-3 ${isMine ? "bg-primary/5" : "bg-muted/30"}`}>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span className="font-medium">{senderName}</span>
                              <span>{formatMessageTime(msg.created_at)}</span>
                            </div>
                            <p className="text-sm mt-1">{msg.message}</p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-lg">{isEnglish ? "Progress Sync" : "Sinkron Progress"}</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!compareTarget}
                    onClick={() => compareTarget && handleCompare(compareTarget)}
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    {isEnglish ? "Refresh" : "Perbarui"}
                  </Button>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 md:p-6 space-y-2">
                  {!compareData ? (
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {isEnglish ? "Select a partner to compare progress." : "Pilih partner untuk membandingkan progress."}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <div className="rounded-lg border border-border p-3">
                        <p className="text-sm font-semibold">{compareData.friend?.name || "Partner"}</p>
                        <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mt-2">
                          <div>
                            <p className="text-muted-foreground">{isEnglish ? "Level" : "Level"}</p>
                            <p className="font-semibold">{compareData.friend.level}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">XP</p>
                            <p className="font-semibold">{compareData.friend.total_xp}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{isEnglish ? "Streak" : "Streak"}</p>
                            <p className="font-semibold">{compareData.friend.overall_streak}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{isEnglish ? "Achievements" : "Achievement"}</p>
                            <p className="font-semibold">{compareData.friend.achievements_count}</p>
                          </div>
                        </div>
                      </div>
                      <div className="rounded-lg border border-border p-3 bg-muted/20">
                        <p className="text-sm font-semibold">{isEnglish ? "Difference (You - Partner)" : "Selisih (Anda - Partner)"}</p>
                        <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm mt-2">
                          <div>
                            <p className="text-muted-foreground">{isEnglish ? "Level" : "Level"}</p>
                            <p className="font-semibold">{compareData.differences.level}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">XP</p>
                            <p className="font-semibold">{compareData.differences.total_xp}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{isEnglish ? "Streak" : "Streak"}</p>
                            <p className="font-semibold">{compareData.differences.overall_streak}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">{isEnglish ? "Challenges" : "Tantangan"}</p>
                            <p className="font-semibold">{compareData.differences.total_challenges_completed}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
